import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";

export interface UseUpdateOnBlurOptions<TSubmit> {
  /** Convert external value to display string for the input */
  toDisplay?: (value: TSubmit) => string;
  /** Convert display string to submit value on blur */
  toSubmit?: (displayValue: string) => TSubmit;
}

/** Return type of useUpdateOnBlur: [displayValue, handleChange, handleBlur] */
export type UseUpdateOnBlurReturn = readonly [
  string,
  (event: ChangeEvent<HTMLInputElement>) => void,
  () => void,
];

/**
 * Hook for input fields that update parent state only on blur instead of onChange.
 * Keeps a local display value synced from the external value, and commits to parent on blur.
 */
export function useUpdateOnBlur<TSubmit>(
  externalValue: TSubmit,
  onUpdate: (value: TSubmit) => void,
  options?: UseUpdateOnBlurOptions<TSubmit>,
): UseUpdateOnBlurReturn {
  const toDisplay = options?.toDisplay ?? ((v) => String(v ?? ""));
  const toSubmit = options?.toSubmit ?? ((v) => v as unknown as TSubmit);
  const toDisplayRef = useRef(toDisplay);
  const toSubmitRef = useRef(toSubmit);
  toDisplayRef.current = toDisplay;
  toSubmitRef.current = toSubmit;

  const [displayValue, setDisplayValue] = useState(() =>
    toDisplay(externalValue),
  );

  useEffect(() => {
    setDisplayValue(toDisplayRef.current(externalValue));
  }, [externalValue]);

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setDisplayValue(event.target.value ?? "");
  }, []);

  const handleBlur = useCallback(() => {
    onUpdate(toSubmitRef.current(displayValue));
  }, [displayValue, onUpdate]);

  return [displayValue, handleChange, handleBlur] as const;
}
